import {
  ActionIcon,
  Box,
  Group,
  Paper,
  TextInput,
  Button,
  Text,
} from "@mantine/core";
import React, { useState, useEffect } from "react";
import { X, Plus } from "tabler-icons-react";

function AudioRecordingWithTimerForm({ form, index }) {
  const currentEpreuve = form.values.epreuves[index];
  const isdicteeSyllabe = currentEpreuve?.type === 'dicteeSyllabe';
  
  // Pour dictée syllabe, on utilise un système similaire à WordByWordTranscription
  const [syllabes, setSyllabes] = useState([]);

  // Pour dictée classique
  const consigneString = currentEpreuve?.consigne?.[0] || "";
  const consigneObj = consigneString ? JSON.parse(consigneString) : { temps_lecture: "", mot: "" };
  const correctionValue = currentEpreuve?.correction_dp?.[0] || "";

  // Initialize syllabes for dicteeSyllabe type
  useEffect(() => {
    if (isdicteeSyllabe) {
      const existingSyllabes = currentEpreuve?.syllabes || [];
      const correctionDp = currentEpreuve?.correction_dp || [];

      if (existingSyllabes.length > 0) {
        setSyllabes(existingSyllabes);
      } else if (correctionDp.length > 0) {
        const syllabesFromData = correctionDp.map((syllabe, syllabeIndex) => ({
          id: Date.now() + syllabeIndex,
          value: syllabe,
        }));
        setSyllabes(syllabesFromData);
        form.setFieldValue(`epreuves.${index}.syllabes`, syllabesFromData);
      }
    }
  }, [currentEpreuve?.correction_dp, currentEpreuve?.syllabes, isdicteeSyllabe]);

  // Debug log
  console.log('AudioRecordingWithTimerForm Debug:', {
    index,
    isdicteeSyllabe,
    currentEpreuve,
    syllabes
  });

  // Fonction pour nettoyer la saisie des mots/phrases (dictée classique)
  const cleanWordInput = (value) => {
    let val = value;
    // Nettoyer la valeur : autoriser seulement les lettres, chiffres, espaces et apostrophes
    // Supprimer tous les caractères spéciaux sauf les espaces et apostrophes
    val = val.replace(/[^a-zA-ZÀ-ÿ0-9\s']/g, '');
    // Limiter les espaces multiples à un seul espace
    val = val.replace(/\s+/g, ' ');
    return val;
  };

  // Fonction pour nettoyer la saisie des syllabes (dictée par syllabe)
  const cleanSyllabeInput = (value) => {
    let val = value;
    // Pour les syllabes : autoriser seulement les lettres et apostrophes (pas de chiffres ni espaces)
    val = val.replace(/[^a-zA-ZÀ-ÿ']/g, '');
    return val;
  };

  const handleWordChange = (e) => {
    const cleanedValue = cleanWordInput(e.target.value);

    // Update correction_dp
    form.setFieldValue(`epreuves.${index}.correction_dp`, [cleanedValue]);

    // Update consigne with temps_lecture and mot as JSON string
    const newObj = { temps_lecture: consigneObj.temps_lecture || "", mot: cleanedValue };
    form.setFieldValue(`epreuves.${index}.consigne`, [JSON.stringify(newObj)]);
  };

  // Fonctions pour gérer les syllabes (dicteeSyllabe)
  const addSyllabe = () => {
    const newSyllabe = {
      id: Date.now(),
      value: "",
    };
    const updatedSyllabes = [...syllabes, newSyllabe];
    setSyllabes(updatedSyllabes);
    form.setFieldValue(`epreuves.${index}.syllabes`, updatedSyllabes);
    // Update correction_dp with all syllabe values
    const values = updatedSyllabes.map(syllabe => syllabe.value).filter(value => value.trim() !== '');
    form.setFieldValue(`epreuves.${index}.correction_dp`, values);
  };

  const removeSyllabe = (syllabeId) => {
    const updatedSyllabes = syllabes.filter(syllabe => syllabe.id !== syllabeId);
    setSyllabes(updatedSyllabes);
    form.setFieldValue(`epreuves.${index}.syllabes`, updatedSyllabes);
    // Update correction_dp with remaining syllabe values
    const values = updatedSyllabes.map(syllabe => syllabe.value).filter(value => value.trim() !== '');
    form.setFieldValue(`epreuves.${index}.correction_dp`, values);
  };

  const updateSyllabe = (syllabeId, value) => {
    const cleanedValue = cleanSyllabeInput(value);
    const updatedSyllabes = syllabes.map(syllabe =>
      syllabe.id === syllabeId ? { ...syllabe, value: cleanedValue } : syllabe
    );
    setSyllabes(updatedSyllabes);
    form.setFieldValue(`epreuves.${index}.syllabes`, updatedSyllabes);
    // Update correction_dp with all syllabe values
    const values = updatedSyllabes.map(syllabe => syllabe.value).filter(value => value.trim() !== '');
    form.setFieldValue(`epreuves.${index}.correction_dp`, values);
  };

  return (
    <Paper mb={20} p={10} shadow="xs">
      <Group noWrap position="apart">
        <Box style={{ flex: 1 }}>
          <TextInput
            label="Titre de l'exercice"
            placeholder={isdicteeSyllabe ? "Ex: Dictée par syllabe" : "Ex: Dictée audio"}
            {...form.getInputProps(`epreuves.${index}.titre`)}
            mb={10}
          />

          {!isdicteeSyllabe && (
            <TextInput
              label="Temps estimé (en secondes)"
              placeholder="Ex: 5"
              type="number"
              value={consigneObj.temps_lecture}
              onChange={(e) => {
                const newObj = {
                  ...consigneObj,
                  temps_lecture: e.target.value === "" ? "" : e.target.value,
                };
                form.setFieldValue(`epreuves.${index}.consigne`, [JSON.stringify(newObj)]);
              }}
              mb={10}
              description="Estimez le temps nécessaire pour compléter cette transcription"
            />
          )}

          {isdicteeSyllabe ? (
            <>
              <Text size="sm" mb={10} color="dimmed">
                Ajoutez les syllabes à dicter
              </Text>

              <Group spacing="xs" mb={10} wrap>
                {syllabes.map((syllabe, syllabeIndex) => (
                  <Group key={syllabe.id} spacing="xs" align="center">
                    <Text size="sm" weight={500}>
                      {syllabeIndex + 1}.
                    </Text>
                    <TextInput
                      placeholder="Syllabe"
                      value={syllabe.value}
                      onChange={(e) => updateSyllabe(syllabe.id, e.target.value)}
                      size="sm"
                      style={{ width: '120px' }}
                    />
                    <ActionIcon
                      color="red"
                      size="sm"
                      onClick={() => removeSyllabe(syllabe.id)}
                    >
                      <X size="1rem" />
                    </ActionIcon>
                  </Group>
                ))}
              </Group>

              <Button
                leftIcon={<Plus size={16} />}
                variant="outline"
                onClick={addSyllabe}
                size="sm"
                mb={10}
              >
                Ajouter une syllabe
              </Button>

              <Text size="xs" color="dimmed">
                Seuls les lettres et apostrophes sont autorisés pour les syllabes (pas de chiffres ni espaces)
              </Text>
            </>
          ) : (
            <TextInput
              label="Mot ou phrase attendue"
              placeholder="Tapez ici le mot ou la phrase attendue pour l'élève"
              value={correctionValue}
              onChange={handleWordChange}
              mb={10}
              description="Seuls les lettres, chiffres, espaces et apostrophes sont autorisés"
            />
          )}
        </Box>
        <ActionIcon
          color="red"
          onClick={() => form.removeListItem('epreuves', index)}
        >
          <X size="1rem" />
        </ActionIcon>
      </Group>
    </Paper>
  );
}

export default AudioRecordingWithTimerForm;
